import React, { useState } from "react";
import toast from 'react-hot-toast';
import {
  crearEntradaExpediente,
  actualizarEntradaExpediente,
  eliminarEntradaExpediente,
  eliminarVacuna,
  eliminarDesparasitacion,
  anularEntradaExpediente,
  certificarEntradaExpediente,
  crearVacuna
} from "../../services/api";
import ExpedienteClinicoForm from "../ExpedienteClinicoForm";
import TratamientoBanner from "./TratamientoBanner";

export default function VisitaDetalle({
  show,
  visitaModalData,
  onClose,
  objetoId,
  isVetAccess,
  usuario,
  entradas,
  setEntradas,
  vacunas,
  setVacunas,
  desparasitaciones,
  setDesparasitaciones,
  entradasConRecordatorios,
  perfilVeterinario,
  onEditVacuna,
  onDeleteVacuna,
  onVerDashboardTratamiento,
  onStartTratamiento,
  onPostSaveTreatment,
  setVisitaModalData,
  setPresetTipo,
  setSelectedEntryId
}) {
  const [showAnularModal, setShowAnularModal] = useState(false);
  const [anularEntryId, setAnularEntryId] = useState(null);
  const [anularRazon, setAnularRazon] = useState('');
  const [loadingAnular, setLoadingAnular] = useState(false);

  function tieneMedicamentosTratables(entry) {
    if (!entry?.medicamentos?.length) return false;
    return entry.medicamentos.some(m => m.frecuenciaHoras && m.duracionDias);
  }

  function handleOpenAnularModal(entryId) {
    setAnularEntryId(entryId);
    setAnularRazon('');
    setShowAnularModal(true);
  }

  async function handleConfirmAnular() {
    if (!anularRazon || anularRazon.trim().length < 10) {
      toast.error('La razón debe tener al menos 10 caracteres');
      return;
    }

    setLoadingAnular(true);
    try {
      const resp = await anularEntradaExpediente(objetoId, anularEntryId, anularRazon.trim());
      if (!resp.error) {
        setEntradas(prev => prev.map(ent =>
          ent.id === anularEntryId
            ? { ...ent, anulado: true, razonAnulacion: anularRazon.trim() }
            : ent
        ));
        if (visitaModalData?.id === anularEntryId) {
          setVisitaModalData(prev => ({ ...prev, anulado: true, razonAnulacion: anularRazon.trim() }));
        }
        toast.success('Registro anulado correctamente');
        setShowAnularModal(false);
        setAnularEntryId(null);
        setAnularRazon('');
      } else {
        toast.error(resp.error + (resp.razon ? `\n${resp.razon}` : ''));
      }
    } catch (error) {
      console.error('[handleConfirmAnular] Exception:', error);
      toast.error('Error al anular el registro');
    } finally {
      setLoadingAnular(false);
    }
  }

  async function handleCertificar(entryId) {
    if (!window.confirm('¿Deseas certificar este registro? Esto confirma que revisaste y validaste la información.')) {
      return;
    }

    try {
      const resp = await certificarEntradaExpediente(objetoId, entryId);
      if (!resp.error) {
        setEntradas(prev => prev.map(ent =>
          ent.id === entryId
            ? { ...ent, certificado: true }
            : ent
        ));
        if (visitaModalData?.id === entryId) {
          setVisitaModalData(prev => ({ ...prev, certificado: true }));
        }
        toast.success('Registro certificado correctamente');
      } else {
        toast.error(resp.error + (resp.razon ? `\n${resp.razon}` : ''));
      }
    } catch (error) {
      console.error('[handleCertificar] Exception:', error);
      toast.error('Error al certificar el registro');
    }
  }

  async function handleDelete(entryId) {
    const entrada = entradas.find(e => e.id === entryId);
    const vacunasAsociadas = vacunas.filter(v => v.eventoMedicoId === entryId);
    const numVacunas = vacunasAsociadas.length;
    const desparasitacionesAsociadas = desparasitaciones.filter(d => d.eventoMedicoId === entryId);
    const numDesparasitaciones = desparasitacionesAsociadas.length;

    let mensaje = '¿Estás seguro de que quieres eliminar esta visita?';
    if (numVacunas > 0 || numDesparasitaciones > 0) {
      const items = [];
      if (numVacunas > 0) items.push(`${numVacunas} vacuna${numVacunas !== 1 ? 's' : ''}`);
      if (numDesparasitaciones > 0) items.push(`${numDesparasitaciones} desparasitacion${numDesparasitaciones !== 1 ? 'es' : ''}`);
      mensaje = `Esta visita tiene ${items.join(' y ')} asociada${(numVacunas + numDesparasitaciones) !== 1 ? 's' : ''}.\n\n¿Eliminar la visita y todos sus registros?`;
    }

    if (!window.confirm(mensaje)) {
      return;
    }

    try {
      if (numVacunas > 0) {
        console.log(`[handleDelete] Eliminando ${numVacunas} vacunas asociadas a visita ${entryId}`);
        for (const vacuna of vacunasAsociadas) {
          const respVacuna = await eliminarVacuna(objetoId, vacuna.id);
          if (respVacuna.error) {
            console.error(`[handleDelete] Error eliminando vacuna ${vacuna.id}:`, respVacuna.error);
          }
        }
        setVacunas(prev => prev.filter(v => v.eventoMedicoId !== entryId));
      }

      if (numDesparasitaciones > 0) {
        console.log(`[handleDelete] Eliminando ${numDesparasitaciones} desparasitaciones asociadas a visita ${entryId}`);
        for (const desp of desparasitacionesAsociadas) {
          const respDesp = await eliminarDesparasitacion(objetoId, desp.id);
          if (respDesp.error) {
            console.error(`[handleDelete] Error eliminando desparasitacion ${desp.id}:`, respDesp.error);
          }
        }
        setDesparasitaciones(prev => prev.filter(d => d.eventoMedicoId !== entryId));
      }

      const resp = await eliminarEntradaExpediente(objetoId, entryId);
      if (!resp.error) {
        setEntradas(prev => prev.filter(entry => entry.id !== entryId));
        setSelectedEntryId(null);

        let successMsg = 'Visita eliminada correctamente';
        if (numVacunas > 0) {
          successMsg += ` (incluyendo ${numVacunas} vacuna${numVacunas !== 1 ? 's' : ''})`;
        }
        toast.success(successMsg);
      } else {
        toast.error('Error al eliminar: ' + resp.error);
      }
    } catch (error) {
      console.error('[handleDelete] Exception:', error);
      toast.error('Error al eliminar la visita');
    }
  }

  if (!show || !visitaModalData) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100001,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-4)',
          paddingTop: 'calc(var(--space-4) + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(var(--space-4) + env(safe-area-inset-bottom, 0px))'
        }}
        onClick={onClose}
      >
        <div
          className="card card-elevated visita-modal-content"
          style={{
            borderRadius: 'var(--radius-xl)',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-header-gradient d-flex justify-content-between align-items-center"
            style={{
              padding: 'var(--space-3) var(--space-4)',
              flexShrink: 0
            }}
          >
            <h5
              className="mb-0"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--white)'
              }}
            >
              <i className={`bi ${visitaModalData.isNew ? 'bi-plus-circle' : 'bi-calendar2-heart'} me-2`}></i>
              {visitaModalData.isNew ? 'Nueva Visita Médica' : 'Cita Médica'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Cerrar"
            ></button>
          </div>

          {/* Información de la visita - solo para edición */}
          {!visitaModalData.isNew && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              borderBottom: '1px solid #bbf7d0',
              flexShrink: 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '14px', color: '#374151' }}>
                <i className="bi bi-calendar3 me-1"></i>
                {visitaModalData.fecha ? new Date(visitaModalData.fecha).toLocaleDateString('es-MX', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                }) : 'Sin fecha'}
              </span>
              {visitaModalData.creadoConIA && (
                <span style={{
                  backgroundColor: '#7c3aed',
                  color: '#fff',
                  fontSize: '10px',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  ✨ Importado con IA
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(() => {
                const tipoIconos = {
                  'consulta': { icon: '🩺', label: 'Consulta', color: '#3b82f6', bg: '#eff6ff' },
                  'vacunacion': { icon: '💉', label: 'Vacunación', color: '#10b981', bg: '#f0fdf4' },
                  'desparasitacion': { icon: '💊', label: 'Desparasitación', color: '#8b5cf6', bg: '#f5f3ff' },
                  'emergencia': { icon: '🚨', label: 'Emergencia', color: '#ef4444', bg: '#fef2f2' },
                  'cirugia': { icon: '🏥', label: 'Cirugía', color: '#f59e0b', bg: '#fefce8' },
                  'analisis': { icon: '🔬', label: 'Análisis', color: '#06b6d4', bg: '#ecfeff' },
                  'estetica': { icon: '✨', label: 'Estética', color: '#ec4899', bg: '#fdf2f8' },
                  'otro': { icon: '📋', label: 'Otro', color: '#6b7280', bg: '#f9fafb' }
                };

                const servicios = visitaModalData.tipos || (visitaModalData.tipo ? [visitaModalData.tipo.toLowerCase().replace(/ /g, '_').replace('á', 'a').replace('ó', 'o')] : []);

                if (servicios.length === 0) {
                  return (
                    <span style={{
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      padding: '4px 10px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {visitaModalData.tipo || 'Visita médica'}
                    </span>
                  );
                }

                return servicios.map((servicio, idx) => {
                  const info = tipoIconos[servicio] || tipoIconos['otro'];
                  return (
                    <span key={idx} style={{
                      backgroundColor: info.bg,
                      color: info.color,
                      padding: '4px 10px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>{info.icon}</span>
                      <span>{info.label}</span>
                    </span>
                  );
                });
              })()}
            </div>
          </div>
          )}

          <div
            style={{
              padding: 'var(--space-4)',
              overflow: 'auto',
              flex: 1
            }}
          >
            <TratamientoBanner
              entry={visitaModalData}
              entradasConRecordatorios={entradasConRecordatorios}
              tieneMedicamentosTratables={tieneMedicamentosTratables}
              isVetAccess={isVetAccess}
              onVerDashboard={onVerDashboardTratamiento}
              onStartTratamiento={onStartTratamiento}
            />
            <ExpedienteClinicoForm
              initialData={visitaModalData.isNew ? (visitaModalData.tipo ? { tipo: visitaModalData.tipo } : null) : visitaModalData}
              onSave={async (formData) => {
                if (visitaModalData.isNew) {
                  const resp = await crearEntradaExpediente(objetoId, formData);
                  if (!resp.error) {
                    setEntradas(prev => [...prev, resp.data]);
                    toast.success('Visita médica registrada correctamente');
                    onClose();
                    setPresetTipo(null);

                    if (formData.tipo === 'Vacunación' && formData.nombreVacuna) {
                      try {
                        const vacunaData = {
                          fecha: formData.fecha?.split('T')[0] || new Date().toISOString().split('T')[0],
                          nombre: formData.nombreVacuna,
                          laboratorio: formData.laboratorio || null,
                          lote: formData.numeroLote || null,
                          caducidad: formData.fechaCaducidad || null,
                          eventoMedicoId: resp.data.id
                        };
                        const vacunaResp = await crearVacuna(objetoId, vacunaData);
                        if (!vacunaResp.error && vacunaResp.data) {
                          setVacunas(prev => [vacunaResp.data, ...prev]);
                        }
                      } catch (error) {
                        console.error('Error creando vacuna en colección:', error);
                      }
                    }

                    const savedEntry = resp.data;
                    if (!isVetAccess && savedEntry.medicamentos?.some(m => m.frecuenciaHoras && m.duracionDias)) {
                      onPostSaveTreatment(savedEntry);
                    }
                  } else {
                    toast.error(resp.error, { duration: 6000 });
                  }
                } else {
                  const resp = await actualizarEntradaExpediente(objetoId, visitaModalData.id, formData);
                  if (!resp.error) {
                    setEntradas(prev => prev.map(ent => ent.id === visitaModalData.id ? resp.data : ent));
                    setVisitaModalData(resp.data);
                    toast.success('Cita médica actualizada');
                  } else {
                    toast.error(resp.error, { duration: 6000 });
                  }
                }
              }}
              onCancel={onClose}
              onDelete={visitaModalData._permisos?.borrar ? async () => {
                await handleDelete(visitaModalData.id);
                onClose();
              } : null}
              onAnular={visitaModalData._permisos?.anular ? () => {
                handleOpenAnularModal(visitaModalData.id);
              } : null}
              onCertificar={visitaModalData._permisos?.certificar ? () => {
                handleCertificar(visitaModalData.id);
              } : null}
              permisos={visitaModalData._permisos}
              usuario={usuario}
              objetoId={objetoId}
              canDeleteFiles={!isVetAccess}
              vacunasAsociadas={visitaModalData.isNew ? [] : vacunas.filter(v => v.eventoMedicoId === visitaModalData.id)}
              onEditVacuna={!isVetAccess ? onEditVacuna : null}
              onDeleteVacuna={!isVetAccess ? onDeleteVacuna : null}
              perfilVeterinario={isVetAccess ? perfilVeterinario : null}
            />
          </div>
        </div>
      </div>

      {/* Modal de Anular Registro */}
      {showAnularModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100001,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
          }}
          onClick={() => !loadingAnular && setShowAnularModal(false)}
        >
          <div
            className="card card-elevated"
            style={{
              borderRadius: 'var(--radius-xl)',
              maxWidth: '450px',
              width: '100%',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#fef3c7'
              }}
            >
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                Anular Registro
              </h5>
            </div>
            <div style={{ padding: 'var(--space-4)' }}>
              <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '16px' }}>
                Al anular este registro, quedará marcado como inválido pero no se eliminará del historial.
                Esta acción no se puede deshacer.
              </p>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                ¿Por qué deseas anular este registro?
              </label>
              <textarea
                value={anularRazon}
                onChange={(e) => setAnularRazon(e.target.value)}
                placeholder="Escribe la razón (mínimo 10 caracteres)"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                disabled={loadingAnular}
              />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {anularRazon.length}/10 caracteres mínimo
              </div>
            </div>
            <div style={{ padding: 'var(--space-4)', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-sm"
                onClick={() => setShowAnularModal(false)}
                disabled={loadingAnular}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  color: '#374151',
                  fontSize: '14px'
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-sm"
                onClick={handleConfirmAnular}
                disabled={loadingAnular || anularRazon.trim().length < 10}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loadingAnular || anularRazon.trim().length < 10 ? 0.5 : 1
                }}
              >
                {loadingAnular ? 'Anulando...' : 'Confirmar Anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
